import React, { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Btn, Field, Input } from "@/components/kit";
import { RelayMark, BrandLockup } from "@/components/brand";

const KEY = "relay_site_gate_token";

// Temporary shared-password lock for the published TEST site. Active only while the
// backend reports a password is set (SITE_ACCESS_PASSWORD env). Once passed, the
// grant is remembered on the device. This is a soft pre-launch gate, not user auth.
export function SiteGate({ children }) {
  const [state, setState] = useState({ loading: true, enabled: false, granted: false });
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api.siteGateStatus()
      .then((r) => {
        if (!alive) return;
        const granted = !r.enabled || !!localStorage.getItem(KEY);
        setState({ loading: false, enabled: !!r.enabled, granted });
      })
      .catch(() => { if (alive) setState({ loading: false, enabled: false, granted: true }); });
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.siteGateVerify(password);
      if (r.token) localStorage.setItem(KEY, r.token);
      setState((s) => ({ ...s, granted: true }));
    } catch (_) {
      setError("That access password is not correct.");
    } finally { setBusy(false); }
  };

  if (state.loading) return null;
  if (state.granted) return children;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5" data-testid="site-gate">
      <div className="w-full max-w-sm">
        <BrandLockup />
        <div className="mt-8 rounded-xl border border-border bg-card p-6 vda-rise">
          <div className="flex items-center gap-2 text-[hsl(var(--vda-clay))] mb-2">
            <Lock className="h-4 w-4" /><span className="vda-tick">Protected preview</span>
          </div>
          <h1 className="text-xl font-semibold">Enter the access password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            This is a private test build. Please enter the shared password to continue.
          </p>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <Field label="Access password">
              <Input data-testid="site-gate-input" type="password" value={password} autoFocus
                onChange={(e) => setPassword(e.target.value)} placeholder="Access password" />
            </Field>
            {error && <p className="text-sm text-[hsl(var(--vda-red))]" data-testid="site-gate-error">{error}</p>}
            <Btn data-testid="site-gate-submit" type="submit" loading={busy} className="w-full">Unlock</Btn>
          </form>
        </div>
        <p className="mt-4 text-center vda-tick text-muted-foreground">AVENIR relay. · private test access</p>
      </div>
    </div>
  );
}
